import React, { CSSProperties } from 'react';
import ButtonSymphony from './ButtonSymphony';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';

/** Button configuration for StickyBanner */
export interface StickyBannerButton {
    /** Button text */
    text: string;
    /** Click handler */
    onClick: () => void;
    /** Button variant */
    variant?: 'primary' | 'outlined' | 'text';
    /** Button color */
    color?: 'purple' | 'yellow' | 'grey' | 'red' | 'green';
    /** Button size */
    size?: 'small' | 'medium' | 'large';
    /** Custom button styles */
    style?: CSSProperties;
}

/** Props for StickyBanner component */
export interface StickyBannerProps {
    /** Optional CSS class name */
    className?: string;
    /** Background color - can be a Colors constant or custom CSS color */
    backgroundColor?: string;
    /** Main banner text */
    mainText: string;
    /** Optional subtitle text */
    subtitleText?: string;
    /** Optional icon/emoji to show before text */
    icon?: string;
    /** Primary button configuration */
    primaryButton?: StickyBannerButton;
    /** Secondary button configuration */
    secondaryButton?: StickyBannerButton;
    /** Whether to show the banner (for conditional rendering) */
    show?: boolean;
    /** Custom banner styles */
    style?: CSSProperties;
    /** Text color override */
    textColor?: string;
    /** Border color override */
    borderColor?: string;
    /** Z-index override */
    zIndex?: number;
}

/**
 * Reusable sticky banner component for displaying notifications, admin states, etc.
 * Supports flexible styling, icons, main/subtitle text, and up to 2 buttons
 */
const StickyBanner: React.FC<StickyBannerProps> = ({
    className,
    backgroundColor = Colors.purpleLight,
    mainText,
    subtitleText,
    icon,
    primaryButton,
    secondaryButton,
    show = true,
    style,
    textColor,
    borderColor,
    zIndex = 1000
}) => {
    // Don't render if show is false
    if (!show) {
        return null;
    }

    const bannerStyle: CSSProperties = {
        ...sty.banner,
        backgroundColor,
        borderBottomColor: borderColor || Colors.purple,
        zIndex,
        ...style
    };

    const textStyle: CSSProperties = {
        ...sty.bannerText,
        color: textColor || (backgroundColor.includes('gradient') ? 'white' : Colors.purple)
    };

    const subtitleStyle: CSSProperties = {
        ...sty.bannerSubtitle,
        color: textColor ? `${textColor}CC` : (backgroundColor.includes('gradient') ? 'rgba(255,255,255,0.8)' : Colors.purpleDark)
    };

    const renderButton = (buttonConfig: StickyBannerButton, key: string) => (
        <ButtonSymphony
            key={key}
            className={`stickyBannerButton ${key}`}
            variant={buttonConfig.variant || 'outlined'}
            color={buttonConfig.color || 'purple'}
            size={buttonConfig.size || 'small'}
            onClick={buttonConfig.onClick}
            style={{
                ...sty.button,
                ...buttonConfig.style
            }}
        >
            {buttonConfig.text}
        </ButtonSymphony>
    );

    return (
        <div 
            className={`stickyBanner ${className || ''}`} 
            style={bannerStyle}
        >
            <div className="bannerContent" style={sty.bannerContent}>
                <div className="bannerTextContainer" style={sty.bannerTextContainer}>
                    {icon && (
                        <span className="bannerIcon" style={sty.bannerIcon}>
                            {icon}
                        </span>
                    )}
                    <div className="bannerTextContent" style={sty.bannerTextContent}>
                        <div className="bannerMainText" style={textStyle}>
                            {mainText}
                        </div>
                        {subtitleText && (
                            <div className="bannerSubtitleText" style={subtitleStyle}>
                                {subtitleText}
                            </div>
                        )}
                    </div>
                </div>
                
                {(primaryButton || secondaryButton) && (
                    <div className="bannerButtons" style={sty.bannerButtons}>
                        {secondaryButton && renderButton(secondaryButton, 'secondary')}
                        {primaryButton && renderButton(primaryButton, 'primary')}
                    </div>
                )}
            </div>
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    banner: {
        borderBottom: '1px solid',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'sticky',
        top: 0,
        width: '100%',
        boxSizing: 'border-box'
    },
    bannerContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: 1200,
        gap: 16
    },
    bannerTextContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: 1
    },
    bannerIcon: {
        fontSize: 18,
        lineHeight: 1
    },
    bannerTextContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2
    },
    bannerText: {
        ...Sty.text14bold,
        margin: 0
    },
    bannerSubtitle: {
        ...Sty.text12,
        margin: 0
    },
    bannerButtons: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0
    },
    button: {
        whiteSpace: 'nowrap'
    }
};

export default StickyBanner;
